import { addMonths, differenceInDays } from 'date-fns';
import React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { fetchDateRange } from '../utils/api/reports/analytics-requests';
import { DashboardChartHeader } from './components/chart-header';
import { DashboardDatePicker } from './components/dashboard-date-picker';
import { DashboardLineChartType1 } from './components/dashboard-line-chart-type1';

function formatData(data) {
  if (data && Object.keys(data)) {
    const returnData = [];
    const workshopIDs = new Set(Object.keys(data.workflows));
    for (const workshopID of workshopIDs) {
      const newEntry = {
        // TODO Have to set label as id, see https://github.com/plouc/nivo/issues/791 .
        // id: workshopID,
        id: data.workflows[workshopID],
        label: data.workflows[workshopID],
        data: Object.entries(data.data).map((d) => ({
          x: d[0],
          y: d[1][workshopID] || 0,
        })),
      };
      newEntry.sum = newEntry.data.reduce((accum, point) => accum + point.y ?? 0, 0);

      // FOR FUTURE READERS
      // The following code is a weekly rollup, when we are ready to support the aggregation dropdown on the charts.
      // const buckets = {};
      // Object.entries(data.data).map((d) => {
      //   const xDate = moment(d[0]);
      //   const xIndex = xDate.year() + ' W' + xDate.week();
      //   if (!buckets[xIndex]) {
      //     buckets[xIndex] = {
      //       x: xIndex, // Probably need a better name here
      //       y: 0,
      //     };
      //   }
      //   buckets[xIndex].y += d[1][workshopID] ?? 0;
      // });

      // const newEntry = {
      //   id: data.workflows[workshopID],
      //   label: data.workflows[workshopID],
      //   data: Object.values(buckets),
      // };
      // newEntry.sum = newEntry.data.reduce((accum, point) => accum + point.y ?? 0, 0);

      returnData.push(newEntry);
    }

    returnData.sort((a, b) => b.sum - a.sum);
    returnData.splice(5, returnData.length - 5);

    // NOTE: Preserve this code in case we want to re-introduce custom markers.
    // // Add the legend/marker symbol widgets in.  Lame that we have to do this in the data,
    // // but Nivo doesn't have a way to do it per-series.
    // let symbol = 0;
    // // Set the symbol widgets.
    // returnData.forEach((entry) => {
    //   entry.symbol = symbol;
    //   entry.data.forEach((point) => {
    //     point.symbol = symbol;
    //   });
    //   symbol += 1;
    // });
    return returnData;
  }
  return [];
}

export function WorkflowNamesTriggeredChart(props) {
  const defaultDateRange = [addMonths(new Date(), -1), new Date()];
  const [dateRange, setDateRange] = React.useState(defaultDateRange);
  const [data, setData] = React.useState([]);
  const [dateRangeError, setDateRangeError] = React.useState('');
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      if (!dateRange) {
        // If the date range is empty, force it to the default date range and do it again.
        setDateRange(defaultDateRange);
        return;
      }
      const daysDiff = differenceInDays(dateRange[1], dateRange[0]);
      if (daysDiff > 366) {
        setDateRangeError('Date range too large.  Please select a range of 1 year or less.');
        return;
      }
      setDateRangeError('');
      const jsonData = await fetchDateRange(
        props.reportClient,
        '/api/v1/analytics/named-workflows-triggered-counts',
        dateRange
      );
      const formattedData = formatData(jsonData);
      setData(formattedData);

      // We delay showing the datepicker until the rest of the UI is ready in order 
      // to avoid a snapping in the UI as it resizes itself.
      setShowDatePicker(true);
    })();
  }, [dateRange]);

  return (
    <div className="chart-box-flow">
      <div className="chart-box-flow-header">
        <DashboardChartHeader title="Top Workflows" tooltip="Top 5 most active Workflows." />
        <DashboardDatePicker {...{ showDatePicker, dateRange, setDateRange, dateLocale: props.dateLocale }} />
      </div>
      {dateRangeError && <div className="ui message chart-date-error">{dateRangeError}</div>}
      <div className={"chart-box-autosizer-wrapper " + (data.length === 0 ? "chart-box-autosizer-wrapper-empty" : "")}>
      {data.length > 0 &&
        <AutoSizer>
          {({ width, height }) => (
            <DashboardLineChartType1
              data={data}
              width={width}
              height={height}
              margin={{ left: 40, bottom: 20, top: 10, right: 240 }}
              singleLegendProps={{
                anchor: 'top-right',
                direction: 'column',
                justify: false,
                translateX: 240,
                translateY: 0,
                itemsSpacing: 5,
                itemWidth: 200,
                itemHeight: 16,
                itemTextColor: '#999',
                itemDirection: 'left-to-right',
              }}
            />
          )}
        </AutoSizer>}
        {data.length === 0 && (
          <div className="ui message chart-date-error">
            No data available for the selected date range.
          </div>
        )}
      </div>
    </div>
  );
}
